/**
 *  This status justification may be used when the product software is not affected because the
 *  code underlying the vulnerability is not present in the product. Unlike the
 *  “Component_not_present'' status justification, the component in question is present, but the
 *  specific code causing the vulnerability is not present in the component.
 *  “Vulnerable_code_not_present” Examples
 *
 *  Examples of this status justification include:
 *  ● The compiler has stripped out the relevant function.
 *  ● The vulnerable code may have been added in a later version of a component and is not
 *   present in the version shipped in the product.
 */
export const VULNERABLE_CODE_NOT_PRESENT = 'Vulnerable_code_not_present';

/**
 * This status justification may be used when the vulnerable code cannot be controlled by an
 * attacker to exploit the vulnerability without exploiting at least one other vulnerability. The
 *  vulnerable component is present and the component contains the vulnerable code. However,
 *  due to some other circumstances, the vulnerability in question cannot be exploited by an
 *  attacker.
 *  “Vulnerable_code_cannot_be_controlled_by_adversary” Examples
 *
 *  Examples of this status justification include:
 *  ● A hard-coded variable that denies user-generated input for the vulnerable function.
 *  ● A logging facility that is only called as a result of hardware malfunction or during
 *  installation.
 *  ● Software is vulnerable to the EternalBlue vulnerability, but SMB ports 139 and 445 are
 *  disabled, and the user does not have access to the O/S to enable the ports. The
 *  vulnerability cannot be exploited by an attacker because the ports are unavailable. No
 *  additional action is required by the operator.
 */
export const VULNERABLE_CODE_CANNOT_BE_CONTROLLED = 'Vulnerable_code_cannot_be_controlled_by_adversary';

/**
 * This status justification may be used when the product is not affected by the vulnerability
 * because the component is not included in the product. The status justification may be used to
 * preemptively inform product users who are seeking to understand a vulnerability that is
 * widespread, receiving a lot of attention, or is in similar products.
 *
 * “Component_not_present” Examples
 * Examples of this status justification include:
 * ● The product is written in Python and Elixir and does not have any Java code present;
 * therefore, the product could not be affected by the Log4j vulnerability.
 * ● Because Java JAR files often contain other JAR files, automation may incorrectly flag a
 * component as existing in a JAR file without verifying it as actually present.
 * ● Base layer container images often contain unused packages. A later layer could remove
 * one or more of these packages. A container image may be expected to contain ‘sudo’
 * but it was removed prior to deployment.
 */
export const COMPONENT_NOT_PRESENT = 'Component_not_present';

/**
 * This status justification may be used when the vulnerable code can never be executed in the
 *   context of the application. This situation can occur when third-party libraries are included but
 *   where the application only uses a fraction of the library functions directly or indirectly. A single
 *   path of execution should not be assumed since the attacker may be able to divert the path of
 *   execution.
 *
 *   “Vulnerable_code_not_in_execute_path” Examples
 *   Examples of this status justification include:
 *   ● An old library ships with the distribution but is not invoked by the software.
 *   ● A vulnerability in a binary has been patched, but the unpatched binary remains for “rollback” purposes.
 *   ● In a container image, there will be operating system utilities that are not used by the
 *   application deployed inside the image. Those utilities cannot be removed from the
 *   container image and are not used by the deployed application in any way.
 *   ● Included libraries often have many functions. A vulnerability in a library in a function that
 *   is never called directly or indirectly also has this status. Applications that only used
 *   OpenSSL for secure random function were not vulnerable to the Heartbleed protocol
 *   vulnerability which was never called from secure random.
 *   ● The code is only executed by a hardware module that is not installed
 */
export const VULNERABLE_CODE_NOT_IN_EXECUTE_PATH = 'Vulnerable_code_not_in_execute_path';

/**
 * This status justification may be used when the product includes built-in protections that prevent
 *   exploitation of the vulnerability. These built-in protections cannot be subverted by the attacker
 *   and cannot be configured or disabled by the user. These mitigations completely prevent
 *   exploitation based on known attack vectors.
 *   Note: Mitigations are effective only in the scenario where the threat model of the software
 *   allows it to perform its protections successfully. A mitigation which negates a vulnerability in
 *   one software/service may not necessarily be the case in another software/service.
 *
 * Examples of this status justification include:
 *   ● Code sanitizes input further upstream.
 *   ● Sandboxing using memory separation techniques through software or hardware.
 *   ● Output and effects of the component that uses vulnerable code is verifiable.
 *   ● A buffer-overflow due to multiplication does not occur if one of the two arguments
 *   always evaluates to 1.
 *   ● Software is vulnerable to the EternalBlue vulnerability and the SMB port is enabled, but
 *   the device integrates a firewall configuration that prevents network access to the ports.
 *   The user does not have access to the firewall to change the configuration to allow the
 *   communication. The device firewall provides inline protection by blocking access to the
 *   port. No additional action is required by the user.
 */
export const INLINE_MITIGATIONS_ALREADY_EXIST = 'Inline_mitigations_already_exist';
